import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BarChart3,
  PieChart,
  Target,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  ArrowRight,
  Search,
  Gauge,
  MapPin,
  Clock,
  Mail,
  Zap,
  DollarSign,
  Eye,
  LineChart,
  Package,
  Activity,
  HelpCircle,
  ChevronRight,
  Layers,
  Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import CookieBanner from "@/components/CookieBanner";

const WHATSAPP_URL = "https://wa.me/34672972514?text=Hola%2C%20quiero%20un%20consultor%20Power%20BI%20para%20mi%20empresa%20en%20Murcia";
const LOGO_URL = "/datyon-logo.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const FAQS = [
  {
    q: "¿Qué hace exactamente un consultor Power BI en Murcia?",
    a: "Como consultor Power BI en Murcia me encargo de conectar tus fuentes de datos (ERP, CRM, Excel) en un único modelo y diseñar dashboards claros para que dirección vea ventas, costes y márgenes en tiempo real, sin pedir informes a nadie."
  },
  {
    q: "¿En cuánto tiempo tendré mi primer dashboard de Power BI funcionando?",
    a: "La primera versión útil de tu dashboard suele estar lista entre 2 y 4 semanas, según el estado de los datos. Trabajo por entregas, así que ves resultados desde la primera semana en lugar de esperar al final del proyecto."
  },
  {
    q: "¿Power BI funciona si mis datos están en Excel y en un ERP antiguo?",
    a: "Sí. Power BI conecta con prácticamente cualquier fuente: Excel, SQL, ERPs antiguos, CRMs y APIs. Mi trabajo es unificar todo en un único modelo aunque las fuentes estén dispersas o en mal estado."
  },
  {
    q: "¿Cuánto cuesta contratar un consultor Power BI para mi empresa?",
    a: "El precio depende del alcance: un dashboard puntual de ventas no cuesta lo mismo que un sistema completo de BI con varias áreas. Tras la sesión inicial te paso una propuesta cerrada con precio fijo, sin sorpresas."
  },
  {
    q: "¿Necesito comprar licencias de Power BI para usar los dashboards?",
    a: "Para uso interno habitual sí, normalmente Power BI Pro o Premium. Te asesoro sobre la licencia adecuada al tamaño de tu empresa y al número de usuarios para que no pagues por funciones que no vas a usar."
  },
  {
    q: "¿Trabajáis con empresas fuera de Murcia o solo en local?",
    a: "Trabajo principalmente con empresas de la Región de Murcia, pero atiendo proyectos en toda España en remoto. La metodología es la misma; lo único que cambia es si las reuniones son presenciales o por videollamada."
  }
];

export default function ConsultorPowerBIMurcia() {
  useEffect(() => {
    document.title = "Consultor Power BI en Murcia | Dashboards y BI para Empresas | Datyon";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Consultor Power BI en Murcia. Dashboards de ventas, costes y rentabilidad que muestran dónde gana y dónde pierde dinero tu empresa. Diagnóstico de 30 minutos gratuito."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://datyon.com/consultor-power-bi-murcia");
    }

    // FAQPage JSON-LD para rich snippets en Google
    const ldScript = document.createElement("script");
    ldScript.type = "application/ld+json";
    ldScript.id = "powerbi-faq-jsonld";
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    });
    document.head.appendChild(ldScript);

    window.scrollTo(0, 0);
    return () => {
      document.title = "Análisis de Datos en Murcia para Empresas | Datyon";
      if (metaDesc)
        metaDesc.setAttribute(
          "content",
          "Transforma los datos de tu empresa en decisiones rentables. Servicios de análisis de datos, business intelligence y modelos predictivos en Murcia."
        );
      if (canonical) canonical.setAttribute("href", "https://datyon.com/");
      const existing = document.getElementById("powerbi-faq-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] relative overflow-x-hidden" data-testid="powerbi-page">
      <div className="noise-overlay" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="powerbi-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center pl-4" data-testid="powerbi-nav-logo">
              <img src={LOGO_URL} alt="DATYON - Consultor Power BI en Murcia" className="h-20 w-auto" style={{ maxHeight: '80px' }} />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Inicio</Link>
              <Link to="/servicios" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Servicios</Link>
              <Link to="/sobre-mi" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Sobre mí</Link>
              <Link to="/blog" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Blog</Link>
              <Link to="/contacto" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Contacto</Link>
            </div>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero */}
        <section className="relative min-h-[80vh] flex items-center section-padding pt-24 overflow-hidden" data-testid="powerbi-hero">
          <div className="spotlight-1 -top-40 -left-40" />
          <div className="spotlight-2 top-1/3 right-0" />
          <div className="max-w-7xl mx-auto w-full relative z-10">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
              <motion.div variants={fadeInUp} className="flex items-center gap-2 mb-4">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <p className="text-indigo-400 font-medium text-sm md:text-base">
                  Datyon — Murcia, España
                </p>
              </motion.div>
              <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" data-testid="powerbi-h1">
                Consultor Power BI en Murcia{" "}
                <span className="text-gradient">para empresas</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-slate-400 text-base md:text-lg mb-4 max-w-2xl">
                Convierto los datos dispersos de tu negocio en dashboards Power BI claros, donde de un vistazo
                ves qué funciona, qué no y dónde se está escapando el dinero. Sin Excel infinitos, sin informes
                que nadie abre, sin esperar a final de mes para enterarte de los problemas.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-slate-300 text-base md:text-lg mb-8 max-w-2xl font-medium">
                En una sesión de 30 minutos puedo decirte dónde estás perdiendo dinero.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base rounded-full" data-testid="powerbi-hero-cta">
                  <Link to="/contacto">
                    Reserva tu sesión de 30 minutos
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-base rounded-full transition-all hover:scale-105 hover:shadow-lg hover:shadow-green-600/30" data-testid="powerbi-hero-whatsapp">
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Hablar por WhatsApp
                  </a>
                </Button>
              </motion.div>
              <motion.p variants={fadeInUp} className="text-slate-500 text-sm mt-4 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Respuesta en menos de 24 horas · Sin compromiso
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Problema */}
        <section className="relative section-padding bg-slate-900/50" data-testid="powerbi-problema">
          <div className="max-w-5xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="mb-10">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="powerbi-h2-problema">
                  El problema: tu empresa tiene datos, pero{" "}
                  <span className="text-gradient">no tiene visibilidad real</span>
                </h2>
                <p className="text-slate-400 text-base md:text-lg leading-relaxed mb-4">
                  La mayoría de empresas con las que hablo en Murcia no tienen un problema de falta de datos.
                  Tienen el problema contrario: datos en el ERP, datos en el CRM, datos en facturación, datos en
                  hojas de cálculo que cada departamento mantiene a su manera. Y nadie tiene una imagen clara
                  de cómo va realmente el negocio.
                </p>
                <p className="text-slate-400 text-base md:text-lg leading-relaxed">
                  El resultado es siempre el mismo: decisiones tomadas a ciegas, problemas que se detectan
                  semanas tarde y oportunidades que pasan desapercibidas. Y mientras tanto, el dinero se escapa
                  por sitios que nadie está mirando.
                </p>
              </motion.div>

              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  { icon: <Eye className="w-5 h-5" />, text: "Cierres mensuales que llegan tarde y obligan a decidir sin información actualizada." },
                  { icon: <Filter className="w-5 h-5" />, text: "Información clave fragmentada entre Excel, ERP, CRM y herramientas que no se hablan entre sí." },
                  { icon: <TrendingDown className="w-5 h-5" />, text: "Sospecha de que hay productos o clientes que están restando margen, pero no hay forma de demostrarlo." },
                  { icon: <Activity className="w-5 h-5" />, text: "Ningún panel donde dirección pueda ver, en directo, cómo va el negocio sin pedir nada a nadie." }
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <div className="glass-card rounded-xl p-5 flex items-start gap-4" data-testid={`powerbi-problema-${i}`}>
                      <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center text-red-400 flex-shrink-0">
                        {item.icon}
                      </div>
                      <p className="text-slate-300 text-sm leading-relaxed">{item.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Qué puedo hacer con Power BI */}
        <section className="relative section-padding overflow-hidden" data-testid="powerbi-que-hago">
          <div className="spotlight-1 top-0 right-0" />
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="powerbi-h2-que-hago">
                  ¿Qué puedo hacer por tu empresa{" "}
                  <span className="text-gradient">con Power BI</span>?
                </h2>
                <p className="text-slate-400 max-w-3xl text-base md:text-lg leading-relaxed">
                  Power BI no es magia ni una herramienta complicada. Es la pieza que conecta toda tu información
                  y te la enseña en una pantalla, ordenada y entendible. Mi trabajo como consultor Power BI en
                  Murcia es montarlo bien para que la primera vez que abras tu dashboard pienses: "esto es exactamente
                  lo que necesitaba ver".
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { icon: <Layers className="w-6 h-6" />, title: "Conecto tus fuentes de datos", desc: "Uno tu ERP, tu CRM, tus Excel, tu pasarela de pagos o cualquier sistema en un único modelo. Una sola fuente de verdad para todo el negocio." },
                  { icon: <BarChart3 className="w-6 h-6" />, title: "Diseño dashboards claros", desc: "Cuadros de mando pensados para que un director vea el estado del negocio en 30 segundos, no para que un técnico se sienta orgulloso." },
                  { icon: <Gauge className="w-6 h-6" />, title: "Defino los KPIs que importan", desc: "Te ayudo a elegir los indicadores que realmente miden si la empresa va bien: margen real, rotación, coste de adquisición, ticket medio, etc." },
                  { icon: <Zap className="w-6 h-6" />, title: "Automatizo la actualización", desc: "Tus datos se refrescan solos. Olvídate de pedir reportes a contabilidad o de copiar y pegar entre archivos cada lunes." }
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <div className="glass-card rounded-xl p-6 h-full" data-testid={`powerbi-que-hago-${i}`}>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-indigo-600/20 flex items-center justify-center text-indigo-400 flex-shrink-0 mt-1">
                          {item.icon}
                        </div>
                        <div>
                          <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                          <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.p variants={fadeInUp} className="text-slate-400 text-base leading-relaxed mt-10 max-w-3xl">
                Si vienes buscando además un perfil más amplio que cubra estrategia de datos completa, también
                puedes ver mi servicio como{" "}
                <Link to="/consultor-datos-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                  consultor de datos en Murcia
                </Link>
                , donde combino dashboards con análisis avanzado y automatizaciones a medida.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Beneficios */}
        <section className="relative section-padding bg-slate-900/50" data-testid="powerbi-beneficios">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="powerbi-h2-beneficios">
                  Beneficios de tener un{" "}
                  <span className="text-gradient">Power BI bien montado</span>
                </h2>
                <p className="text-slate-400 max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
                  Cuando los dashboards funcionan, dejan de ser un capricho de oficina y se convierten en una
                  herramienta que cambia cómo se gestiona la empresa. Esto es lo que notarás:
                </p>
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: <DollarSign className="w-6 h-6" />, title: "Ahorras dinero", desc: "Detectas a tiempo los productos, clientes o procesos que están drenando margen y puedes corregirlos antes de que el agujero crezca." },
                  { icon: <Target className="w-6 h-6" />, title: "Decides con datos", desc: "Las reuniones de dirección dejan de ser opiniones y pasan a ser conversaciones sobre números reales y comparables." },
                  { icon: <Eye className="w-6 h-6" />, title: "Tienes el control", desc: "Sabes en cualquier momento cómo va el mes, sin esperar al cierre y sin depender de que alguien te pase un Excel." },
                  { icon: <Clock className="w-6 h-6" />, title: "Ganas tiempo", desc: "Tu equipo deja de fabricar informes a mano. Lo que antes costaba un día, ahora se actualiza solo en segundos." }
                ].map((b, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover text-center" data-testid={`powerbi-beneficio-${i}`}>
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 mb-4 mx-auto">
                          {b.icon}
                        </div>
                        <h3 className="font-semibold text-white mb-2">{b.title}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{b.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Casos de uso */}
        <section className="relative section-padding overflow-hidden" data-testid="powerbi-casos">
          <div className="spotlight-2 -bottom-20 -left-20" />
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="powerbi-h2-casos">
                  Casos de uso reales de{" "}
                  <span className="text-gradient">dashboards Power BI</span>
                </h2>
                <p className="text-slate-400 max-w-3xl mx-auto text-base leading-relaxed">
                  Estos son los cuadros de mando que pido prácticamente en todas las empresas con las que trabajo.
                  Los desarrollo siempre adaptados al sector y a la forma de operar del cliente, pero la lógica de
                  negocio detrás se repite.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    icon: <BarChart3 className="w-6 h-6" />,
                    sector: "Ventas",
                    title: "Dashboard de ventas",
                    desc: "Facturación por cliente, producto, comercial y zona. Comparativas con año anterior, evolución mensual y detección automática de caídas. Ideal para reuniones comerciales semanales."
                  },
                  {
                    icon: <Package className="w-6 h-6" />,
                    sector: "Costes",
                    title: "Dashboard de costes",
                    desc: "Visión clara de en qué se está gastando el dinero la empresa: compras, personal, logística, costes indirectos. Alertas cuando una partida se desvía del presupuesto previsto."
                  },
                  {
                    icon: <PieChart className="w-6 h-6" />,
                    sector: "Rentabilidad",
                    title: "Dashboard de rentabilidad",
                    desc: "Margen real por producto y por cliente, descontando todos los costes asociados. Es el dashboard que más sorpresas suele dar y el que más decisiones desbloquea en dirección."
                  },
                  {
                    icon: <Gauge className="w-6 h-6" />,
                    sector: "KPIs",
                    title: "Cuadro de KPIs empresariales",
                    desc: "Los 8-10 indicadores clave del negocio en una sola pantalla: facturación, margen, cartera de clientes, tesorería, rotación de stock, ratio de cobro… El cuadro de mando del CEO."
                  }
                ].map((caso, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover" data-testid={`powerbi-caso-${i}`}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-lg bg-indigo-600/20 flex items-center justify-center text-indigo-400">
                            {caso.icon}
                          </div>
                          <span className="text-xs font-medium text-indigo-400 bg-indigo-400/10 px-3 py-1 rounded-full">
                            {caso.sector}
                          </span>
                        </div>
                        <h3 className="font-semibold text-white mb-3 text-lg">{caso.title}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{caso.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              <motion.div variants={fadeInUp} className="text-center mt-10">
                <Link to="/business-intelligence-murcia" className="text-indigo-400 hover:text-indigo-300 text-sm font-medium inline-flex items-center gap-2 transition-colors" data-testid="powerbi-link-bi">
                  Conoce mis servicios de business intelligence en Murcia <ChevronRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Proceso de trabajo */}
        <section className="relative section-padding bg-slate-900/50" data-testid="powerbi-proceso">
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="powerbi-h2-proceso">
                  Cómo trabajamos{" "}
                  <span className="text-gradient">paso a paso</span>
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto text-base leading-relaxed">
                  Un proceso simple, directo y orientado a resultados. Sin reuniones interminables ni proyectos
                  que se eternizan.
                </p>
              </motion.div>

              <div className="space-y-6">
                {[
                  { step: "01", title: "Sesión de 30 minutos", desc: "Hablamos de tu negocio y de qué decisiones te gustaría tomar con datos. En esa misma llamada te digo qué dashboards tendrían más impacto y por dónde empezaría." },
                  { step: "02", title: "Diagnóstico de tus datos", desc: "Reviso qué información tienes ya disponible (ERP, CRM, Excel, etc.), cómo está organizada y qué hace falta para llevarla a Power BI sin complicaciones." },
                  { step: "03", title: "Diseño del modelo y los dashboards", desc: "Construyo el modelo de datos en Power BI, defino contigo los KPIs y diseño los cuadros de mando que necesitas. Iteramos hasta que estás cómodo con lo que ves." },
                  { step: "04", title: "Automatización y publicación", desc: "Configuro la actualización automática para que los datos se refresquen solos y publico los dashboards para que tú y tu equipo accedáis desde cualquier dispositivo." },
                  { step: "05", title: "Formación y soporte", desc: "Te enseño a navegar el Power BI, a interpretar los KPIs y a sacar tus propias conclusiones. Y quedo disponible para ajustar o ampliar el panel cuando tu negocio lo pida." }
                ].map((s, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <div className="glass-card rounded-xl p-6 flex items-start gap-6" data-testid={`powerbi-paso-${i}`}>
                      <div className="text-3xl font-extrabold text-indigo-400/30 flex-shrink-0 leading-none">
                        {s.step}
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-2">{s.title}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div variants={fadeInUp} className="text-center mt-10">
                <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base rounded-full" data-testid="powerbi-proceso-cta">
                  <Link to="/contacto">
                    Empezar con una sesión de 30 minutos
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* CTA Intermedia */}
        <section className="relative section-padding" data-testid="powerbi-cta-intermedia">
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="glass-card rounded-2xl p-8 md:p-10 text-center">
                <h2 className="text-2xl sm:text-3xl font-bold mb-3" data-testid="powerbi-h2-cta-intermedia">
                  ¿Quieres ver dónde se está escapando el dinero{" "}
                  <span className="text-gradient">en tu empresa</span>?
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto mb-6 text-base leading-relaxed">
                  En una sesión de 30 minutos puedo decirte dónde estás perdiendo dinero, qué dashboards te
                  resolverían el problema y si Power BI es la herramienta correcta para tu caso. Sin compromiso
                  y sin venta forzada.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-7 py-5 text-base rounded-full" data-testid="powerbi-cta-intermedia-contact">
                    <Link to="/contacto">
                      Reservar sesión gratuita
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-7 py-5 text-base rounded-full" data-testid="powerbi-cta-intermedia-whatsapp">
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                      Escribir por WhatsApp
                    </a>
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative section-padding overflow-hidden" data-testid="powerbi-faq">
          <div className="max-w-3xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="powerbi-h2-faq">
                  Preguntas frecuentes sobre{" "}
                  <span className="text-gradient">Power BI para empresas</span>
                </h2>
                <p className="text-slate-400 text-base leading-relaxed">
                  Estas son las dudas que me plantean casi todas las empresas antes de empezar un proyecto de Power BI.
                </p>
              </motion.div>

              <div className="space-y-4">
                {FAQS.map((faq, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="bg-slate-900 border-slate-800" data-testid={`powerbi-faq-${i}`}>
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-white mb-3 flex items-start gap-3">
                          <HelpCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                          {faq.q}
                        </h3>
                        <p className="text-slate-400 text-sm leading-relaxed pl-8">{faq.a}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* SEO Content Block */}
        <section className="relative section-padding bg-slate-900/50" data-testid="powerbi-seo-content">
          <div className="max-w-4xl mx-auto">
            <div className="text-slate-400 text-base leading-relaxed space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
                Consultor Power BI en Murcia: dashboards que sí se usan
              </h2>

              <p>
                La diferencia entre un Power BI que de verdad cambia el negocio y un Power BI que termina abandonado
                en un cajón virtual no está en la herramienta. Está en cómo se diseña. He visto empresas con licencias
                pagadas durante años y dashboards a los que nadie entra porque están sobrecargados, lentos o
                respondiendo a preguntas que nadie en dirección se hace. Mi enfoque como consultor Power BI en Murcia
                es justo el contrario: pocos paneles, muy bien pensados, alineados con las decisiones que el equipo
                directivo toma cada semana.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-4">
                Dashboards Murcia: del Excel reactivo al control en tiempo real
              </h3>

              <p>
                La mayoría de empresas de Murcia con las que empiezo un proyecto venían de gestionar su negocio con
                hojas de cálculo. Funciona durante un tiempo, pero llega un momento en que el Excel deja de servir:
                cierres mensuales que requieren días, fórmulas que se rompen, versiones distintas según quién mande
                el archivo. Power BI rompe ese cuello de botella. Una vez montado, los datos se actualizan solos y
                cualquier persona del equipo accede desde su móvil, su portátil o el navegador, sin pedir ayuda y
                sin esperar.
              </p>

              <p>
                En la práctica, esto se traduce en que dirección puede mirar la facturación del día sin levantarse
                de la silla, comercial puede ver qué clientes están comprando menos este mes y operaciones puede
                detectar al momento si un proceso se está desviando. Es un cambio cultural, no solo tecnológico:
                se pasa de discutir opiniones a discutir datos.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-4">
                Power BI para empresas: dónde encaja dentro de una estrategia de datos
              </h3>

              <p>
                Power BI es la capa visible del Business Intelligence, pero detrás siempre hay un trabajo previo
                de modelado y limpieza de datos que es el que de verdad determina si el dashboard funcionará. Por
                eso este servicio se complementa de forma natural con mis{" "}
                <Link to="/business-intelligence-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                  servicios de business intelligence
                </Link>{" "}
                y con mi trabajo más amplio de{" "}
                <Link to="/analisis-datos-empresas" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                  análisis de datos para empresas
                </Link>
                . Si lo que buscas es alguien que entienda el negocio antes de tocar la herramienta, esa es exactamente
                la forma en la que trabajo.
              </p>

              <p>
                También puedes leer mi artículo del blog sobre{" "}
                <Link to="/blog/como-saber-si-tu-empresa-pierde-dinero" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                  cómo saber si tu empresa está perdiendo dinero
                </Link>
                , donde explico de forma sencilla qué señales miro siempre antes de proponer un dashboard concreto.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-4">
                Por qué elegirme como consultor Power BI en Murcia
              </h3>

              <p>
                Trabajo de forma directa, sin intermediarios ni equipos de cuenta. Hablas conmigo desde el primer minuto
                y sigues hablando conmigo durante todo el proyecto. Eso significa propuestas claras, plazos realistas
                y soluciones diseñadas para tu caso, no para encajar en un catálogo de servicios estándar. Y, sobre
                todo, una obsesión: que cada euro que inviertas en datos vuelva a la cuenta de resultados en forma de
                mejor margen, menos costes ocultos o mejores decisiones.
              </p>

              <p>
                Antes de hablar conmigo puedes hacerte una idea rápida usando la{" "}
                <Link to="/calculadora-roi-power-bi-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                  calculadora ROI Power BI Murcia
                </Link>
                : en menos de un minuto te dice cuánto dinero podría estar perdiendo tu empresa por no tener
                visibilidad real de los datos.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative section-padding bg-gradient-to-b from-slate-900/80 to-[#020617]" data-testid="powerbi-cta-final">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-bold mb-4" data-testid="powerbi-h2-cta-final">
                Pongamos a trabajar{" "}
                <span className="text-gradient">tus datos</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-slate-400 mb-3 max-w-xl mx-auto text-base leading-relaxed">
                Si quieres dejar de gestionar tu empresa con la sensación de ir a ciegas, hablemos. En una sesión
                de 30 minutos puedo decirte dónde estás perdiendo dinero y cómo un Power BI bien diseñado puede
                ayudarte a recuperarlo.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-indigo-400 font-medium mb-8 text-base">
                Diagnóstico gratuito, sin compromiso.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base rounded-full" data-testid="powerbi-cta-final-contact">
                  <Link to="/contacto">
                    Reservar mi sesión de 30 minutos
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-base rounded-full transition-all hover:scale-105 hover:shadow-lg hover:shadow-green-600/30" data-testid="powerbi-cta-final-whatsapp">
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Hablar por WhatsApp
                  </a>
                </Button>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-6 text-slate-400 text-sm">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-indigo-400" />
                  Respuesta en menos de 24 horas
                </span>
                <span className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <a href="mailto:info@datyon.com" className="hover:text-white transition-colors">info@datyon.com</a>
                </span>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800" data-testid="powerbi-footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="DATYON" className="h-8 w-auto" />
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span className="text-sm">Murcia, España</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-500 hover:text-white text-sm transition-colors">Inicio</Link>
              <Link to="/servicios" className="text-slate-500 hover:text-white text-sm transition-colors">Servicios</Link>
              <Link to="/blog" className="text-slate-500 hover:text-white text-sm transition-colors">Blog</Link>
              <Link to="/contacto" className="text-slate-500 hover:text-white text-sm transition-colors">Contacto</Link>
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-6 pt-4 border-t border-slate-800">
            <Link to="/aviso-legal" className="text-slate-500 hover:text-white text-sm transition-colors">Aviso Legal</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-privacidad" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Privacidad</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-cookies" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Cookies</Link>
          </div>
          <p className="text-slate-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} DATYON - Antonio Oliver. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* WhatsApp Floating */}
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg shadow-green-500/30 transition-all duration-300 hover:scale-110 group"
        data-testid="powerbi-whatsapp-float" aria-label="Contactar por WhatsApp">
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          ¿Hablamos?
        </span>
      </a>

      <CookieBanner />
    </div>
  );
}
